/**
 * Pinned apps for the app rail (PROD-1403).
 *
 * Stored in a COOKIE, not localStorage: every Ordo app is its own origin
 * (`tasks.ordo.com`, `hq.ordo.com`, …), and localStorage is per-origin, so a
 * pin made in Tasker would be invisible in HQ. A cookie scoped to the page's
 * parent domain is shared by every app in the same ring:
 *
 *   tasks.ordo.com                  → Domain=.ordo.com
 *   tasks.cms1234.preview.ordo.com  → Domain=.cms1234.preview.ordo.com
 *   localhost:3060                  → host-only (cookies ignore ports, so
 *                                     every local dev server shares it)
 *
 * The value is a comma-separated list of app ids in pin order. Pins are a
 * per-browser convenience; an id that no longer resolves to a visible app is
 * simply skipped when rendering.
 */
import { useSyncExternalStore } from 'react'

export const APP_PINS_COOKIE = 'ordo_app_pins'
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365
const ORDO_DOMAIN = 'ordo.com'

/** `Domain=` attribute for the page's ring, or null for host-only. Exported for tests. */
export function pinsCookieDomain(host: string): string | null {
  const parent = host.split('.').slice(1).join('.')
  if (parent === ORDO_DOMAIN || parent.endsWith(`.${ORDO_DOMAIN}`)) {
    return `.${parent}`
  }
  return null
}

/** Parse a raw `document.cookie` string into pinned ids. Exported for tests. */
export function parsePinsCookie(cookie: string): string[] {
  for (const part of cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=')
    if (name === APP_PINS_COOKIE) {
      const raw = decodeURIComponent(rest.join('='))
      return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
  }
  return []
}

const EMPTY: string[] = []
let pins: string[] = EMPTY
let loaded = false
const listeners = new Set<() => void>()

function readFromCookie(): string[] {
  if (typeof document === 'undefined') {
    return EMPTY
  }
  return parsePinsCookie(document.cookie)
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i])
}

// Another app (another origin, another tab) may have changed the cookie.
// Re-read on focus so returning to a tab picks up pins made elsewhere.
function resync(): void {
  const next = readFromCookie()
  if (!sameList(next, pins)) {
    pins = next
    for (const l of listeners) {
      l()
    }
  }
}

function writePins(next: string[]): void {
  pins = next
  if (typeof document !== 'undefined') {
    const domain = pinsCookieDomain(location.hostname)
    const secure = location.protocol === 'https:' ? '; Secure' : ''
    document.cookie =
      `${APP_PINS_COOKIE}=${encodeURIComponent(next.join(','))}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax` +
      (domain ? `; Domain=${domain}` : '') +
      secure
  }
  for (const l of listeners) {
    l()
  }
}

function subscribe(listener: () => void): () => void {
  if (!loaded) {
    loaded = true
    pins = readFromCookie()
  }
  listeners.add(listener)
  if (listeners.size === 1 && typeof window !== 'undefined') {
    window.addEventListener('focus', resync)
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('focus', resync)
    }
  }
}

function getSnapshot(): string[] {
  return pins
}

function getServerSnapshot(): string[] {
  return EMPTY
}

/** Replace the whole pin list (drag-to-reorder). Duplicates are dropped. */
export function setPinnedApps(ids: string[]): void {
  writePins([...new Set(ids)])
}

/**
 * Where a dragged app ends up (PROD-1403, Notion-style). `target` = the id
 * of the pinned app it was dropped in front of; `null` = the end of the
 * pinned group; `'unpin'` = dropped among the unpinned apps. Pure; exported for tests.
 */
export function movePin(pins: string[], id: string, target: string | null | 'unpin'): string[] {
  // Dropped on its own front edge: it is already there.
  if (target === id) {
    return pins
  }
  const without = pins.filter((p) => p !== id)
  if (target === 'unpin') {
    return without
  }
  const at = target === null ? -1 : without.indexOf(target)
  if (at === -1) {
    return [...without, id]
  }
  return [...without.slice(0, at), id, ...without.slice(at)]
}

/**
 * The drop target while the pointer is over pinned app `overId`: in front of
 * it on its left half, in front of the next pinned app on its right half, and
 * the end of the group on the last app's right half, so the far-right slot
 * is as easy to hit as any other. `pins` is the pinned order as drawn. Pure;
 * exported for tests.
 */
export function pinTargetAt(pins: readonly string[], overId: string, rightHalf: boolean): string | null {
  if (!rightHalf) {
    return overId
  }
  return pins[pins.indexOf(overId) + 1] ?? null
}

export function togglePinnedApp(id: string): void {
  writePins(pins.includes(id) ? pins.filter((p) => p !== id) : [...pins, id])
}

export function usePinnedApps(): {
  pinnedIds: string[]
  togglePin: (id: string) => void
  setPins: (ids: string[]) => void
} {
  const pinnedIds = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  return { pinnedIds, togglePin: togglePinnedApp, setPins: setPinnedApps }
}
